import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor: on 401 (expired/invalid token), clear session so the
// app doesn't get stuck showing blank pages with a dead token. This was the
// root cause of "everything went blank after login" on mobile — a stale token
// was in localStorage, every API returned 401 silently, and the UI just spun.
let _authCleanupInFlight = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Don't intercept the login/register/refresh calls — they need to bubble
    // the 401 back to the form so the user sees "Invalid credentials".
    const isAuthCall = /\/auth\/(login|register|forgot|reset|verify|send-|refresh)/.test(url);
    const detail = typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "";
    const suspended = status === 403 && /suspended|account has been deleted/i.test(detail);
    if ((status === 401 || suspended) && !isAuthCall && !_authCleanupInFlight) {
      _authCleanupInFlight = true;
      try {
        localStorage.removeItem("hf_token");
        localStorage.removeItem("user");
      } catch { /* noop */ }
      // If we're not already on a public page, bounce to /login
      const path = window.location.pathname;
      const publicPaths = ["/login", "/signup", "/forgot", "/reset", "/verify", "/recover-account", "/terms", "/privacy", "/policy"];
      const onPublic = publicPaths.some(p => path.startsWith(p));
      if (!onPublic) {
        setTimeout(() => { window.location.replace(suspended ? "/login?suspended=1" : "/login"); }, 50);
      }
      setTimeout(() => { _authCleanupInFlight = false; }, 2000);
    }
    return Promise.reject(error);
  }
);

export default api;

export const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) {
    // Network-level failure (no response) — usually mobile network, CORS, or preview URL stale on PWA
    const msg = e?.message || "";
    if (e?.code === "ERR_NETWORK" || /network error/i.test(msg) || /failed to fetch/i.test(msg)) {
      return "Can't reach Hifireels servers. Please check your internet connection and try again. If you're on the preview URL from a saved home-screen icon, please open hifireels.com directly instead.";
    }
    return msg || "Something went wrong";
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" • ");
  return String(d);
};
